<template>
  <div class="cart-drawer critical-component-hide">
    <div class="cart-drawer__header">
      <h2
        class="cart-drawer__title text-heading-2xl text-heading-3xl-desktop"
        v-text="$string('cart.title')"
      />

      <p
        v-if="cart.item_count > 0"
        class="cart-drawer__meta text-body-s-bold"
      >
        <span
          class="cart-drawer__item-count"
          v-text="$string(
            'cart.item_count',
            {
              replace: { count: cart.item_count },
              pluralise: cart.item_count,
            }
          )"
        />

        <span
          class="cart-drawer__total-price"
          v-text="$formatMoney(cart.total_price)"
        />
      </p>
    </div>

    <form
      v-if="cart.item_count > 0 || cartStatus.label === 'adding'"
      class="cart-drawer__form"
      :action="`${$variable('routes.cart.url')}?locale=${$variable('store.language')}`"
      method="post"
    >
      <div
        class="cart-drawer__line-items"
        js-overlay="scrollElement"
      >
        <template
          v-for="(lineItem, index) of cartItems"
          :key="getCartItemKey(lineItem)"
        >
          <line-item
            class="cart-drawer__line-item"
            :customer-role="customerRole"
            :line-item="lineItem"
            small
          />

          <divider-line v-if="(index + 1) !== cartItems.length" />
        </template>

        <div
          v-if="cartContainsEvalProductInCart && showEvalCopyError"
          class="cart-drawer__eval-copy"
        >
          <div class="cart-drawer__eval-copy-wrapper">
            <div>
              <p
                class="cart-drawer__eval-copy-title text-body-s-bold"
                v-text="getErrorData.title"
              />
              <p
                class="cart-drawer__eval-copy-description text-body-s-regular"
                v-text="getErrorData.description"
              />
            </div>
            <button
              type="button"
              class="cart-drawer__eval-copy-button"
              :aria-label="$string('accessibility.close')"
              @click="showEvalCopyError = false"
            >
              <icon-close />
            </button>
          </div>
        </div>
      </div>

      <cart-summary
        v-if="cart.item_count > 0"
        class="cart-drawer__footer"
        small
      />
    </form>

    <p
      v-else
      class="cart-drawer__empty text-body-s-regular"
      v-text="$string('cart.empty.drawer_text')"
    />
  </div>
</template>

<script>

/**
 * Vue: Cart drawer (cart-drawer)
 * -----------------------------------------------------------------------------
 * Displays contents of cart.
 *
 * @param {Object} overlay - Overlay API.
 *
 */
import { mapGetters, mapState } from 'vuex'

import CartSummary from '~async/cart-summary/cart-summary'
import DividerLine from '~async/utils/divider-line/divider-line'
import LineItem from '~async/line-item/line-item'
import IconClose from '~icons/directional-navigation/close.svg'

export default {
  name: 'CartDrawer',

  components: {
    CartSummary,
    DividerLine,
    LineItem,
    IconClose,
  },

  props: {
    overlay: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      showEvalCopyError: true,
    }
  },

  computed: {

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      getErrorData: 'product-eval-copy/getErrorData',
    }),

    /**
     * Map Vuex state.
     */
    ...mapState({
      cart: (state) => state.cart.response,
      cartStatus: (state) => state.cart.status,
    }),

    /**
     * Compute cart items.
     * - For each item that is being added, check if it's already in the cart.
     * - Check against key (if adding from cart) or id (if adding from other).
     * - If it isn't then add a false item to act as a loading placeholder.
     * @returns {Array}
     */
    cartItems() {
      if (this.cartStatus.label !== 'adding') {
        return this.cart.items
      }

      const placeholders = []

      this.cartStatus.items.forEach((addedItem) => {
        const match = this.cart.items.find((cartItem) => {
          const cartItemProperty = typeof addedItem === 'string'
            ? cartItem.key
            : cartItem.id

          return cartItemProperty === addedItem
        })

        if (match) {
          return
        }

        placeholders.push(false)
      })

      return [
        ...placeholders,
        ...this.cart.items,
      ]
    },

    /**
     * Returns the customer role.
     * @return {String}
     */
    customerRole() {
      return this.cart?.attributes?.customer_role
    },

    /*
     * Checks if there is regular product in basket
     */
    cartContainsEvalProductInCart() {
      return this.cartItems.some((item) => {
        return item.properties?.[this.$variable('constants.LINE_ITEM_PROPERTY_EVALUATION')]
      })
    },
  },

  watch: {

    /**
     * Watch cart items to update focus trap.
     */
    'cart.items'() {
      if (!this.overlay.isActive) {
        return
      }

      this.overlay.update({ currentElement: document.activeElement })
    },
  },

  mounted() {
    if (!this.overlay.isActive) {
      return
    }

    this.overlay.update()
  },

  methods: {

    /**
     * Get cart item key.
     * @param {Object} item - Line item.
     * @param {Number} index - Line item index.
     * @returns {String}
     */
    getCartItemKey(item, index) {
      if (!item) {
        return `cart-line-item-loading-${index}`
      }

      return `cart-line-item-${item.key}`
    },

    /**
     * Shopify section editor select event.
     */
    shopifySectionSelect() {
      this.overlay.open({
        component: 'cart-drawer',
        ignoreDismissed: true,
      })
    },

    /**
     * Shopify section editor deselect event.
     */
    shopifySectionDeselect() {
      this.overlay.close()
    },
  },
}
</script>

<style lang="scss">
@import './cart-drawer';
</style>
